import React from 'react'
import styled from 'styled-components'
import { getTypographyStyles } from '../Typography'

export type DropdownItemProps = {
  children?: React.ReactNode
  onClick?: (e: React.MouseEvent) => void
  as?: React.ComponentProps<typeof DropdownItemContainer>['as']
  className?: string
  onMouseEnter?: () => void
  disabled?: boolean
  style?: React.CSSProperties
}

const DropdownItemContainer = styled.button<React.HTMLProps<HTMLButtonElement>>`
  all: unset;
  box-sizing: border-box;
  width: 100%;
  padding: 10px 15px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  border-bottom: var(--gray100);
  border-radius: var(--border-radius-xs);
  border: 1px solid transparent;
  ${getTypographyStyles('body')}

  user-select: none;

  * {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  }

  &:focus-within {
    background-color: var(--gray100);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &:hover {
    background-color: var(--gray100);
    color: var(--brand-primary);
  }
`

function DropdownItem(
  {
    className,
    as,
    children,
    onClick,
    onMouseEnter,
    disabled,
    style,
    ...rest
  }: DropdownItemProps & React.HtmlHTMLAttributes<HTMLButtonElement>,
  ref: React.Ref<HTMLButtonElement>
) {
  const itemRef = React.useRef<HTMLButtonElement | null>(null)

  const onClickHandler = (e: React.MouseEvent) => {
    e.preventDefault()

    if (disabled) return

    onClick?.(e)
  }

  return (
    <DropdownItemContainer
      style={style}
      className={`${className} ${disabled ? 'disabled' : ''} dispense-dropdown-item`}
      onMouseDown={(e: React.MouseEvent) => e.button === 1 && onClickHandler(e)}
      onClick={onClickHandler}
      as={as}
      ref={(e: HTMLButtonElement) => {
        itemRef.current = e
        if (typeof ref === 'function') {
          // istanbul ignore next
          ref(e)
        } else if (ref) {
          // istanbul ignore next
          (ref as React.MutableRefObject<HTMLButtonElement | null>).current = e;
        }
      }}
      onMouseEnter={
        onMouseEnter ? () => onMouseEnter() : () => itemRef.current?.focus({ preventScroll: true })
      }
      {...rest}
    >
      {children}
    </DropdownItemContainer>
  )
}

export default React.forwardRef(DropdownItem)
